import { WarningCycleIcon } from "@/components/common/icons/WarningCycleIcon/WarningCycleIcon"
import { Typography } from "@/components/common/Typography/Typography"
import { AnswerSourceGroup } from "@/components/feature/AnswerSourceGroup/AnswerSourceGroup"
import { TextAreaListItem } from "@/components/feature/CollapsibleQuestionnaireList/TextAreaListItem/TextAreaListItem"
import { AskForHelpModal } from "@/components/feature/Modals/AskForHelpModal/AskForHelpModal"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import {
  ChangeActionPayload,
  EditResponsePairKey,
  TextAreaStateActionType,
  TextAreaStateDispatch,
} from "@/types/hooks/useTextAreaStates"
import { QuestionnaireResponsePair } from "@/types/questionnaire/models"
import { classNames } from "@/utils/classNames"
import { IconMessage } from "@tabler/icons-react"
import { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router"
interface AnswerItemProps {
  responsePair: QuestionnaireResponsePair
  sectionId: string
  updateText: TextAreaStateDispatch
}

const EDIT_RESPONSE_PAIR_KEY: EditResponsePairKey = "response"
const TEXT_UPDATE_DELAY_MILISEC = 1000

export const AnswerItem: React.FC<AnswerItemProps> = ({
  responsePair,
  sectionId,
  updateText,
}) => {
  const { translate } = useTranslation()
  const query = new URLSearchParams(useLocation().search)
  const urlQueryId = query.get("question")
  const [isAskForHelpModalOpen, setIsAskForHelpModalOpen] = useState(false)

  const handleOpenShareModal = () => {
    setIsAskForHelpModalOpen(true)
  }

  const initialText = responsePair[EDIT_RESPONSE_PAIR_KEY] ?? ""
  const [text, setText] = useState(initialText)
  const hasBeenEdited = useRef(false)

  useEffect(() => {
    if (!hasBeenEdited.current) {
      return
    }

    const timeout = setTimeout(() => {
      updateText({
        type: TextAreaStateActionType.Change,
        payload: {
          sectionId: sectionId,
          responsePairId: responsePair.id,
          text: text,
        } satisfies ChangeActionPayload,
      })

      trackEvent(AnalyticsEvent.ANSWER_EDITED, {
        response_pair_id: responsePair.id,
        edit_type: "manual",
      })
    }, TEXT_UPDATE_DELAY_MILISEC)

    return () => clearTimeout(timeout)
  }, [text, updateText, responsePair.id, sectionId])

  const onTextChange = (changedText: string) => {
    hasBeenEdited.current = true
    setText(changedText)
  }

  return (
    <>
      {isAskForHelpModalOpen && (
        <AskForHelpModal
          responsePairId={responsePair.id}
          setIsAskForHelpModalOpen={setIsAskForHelpModalOpen}
        />
      )}

      <div
        id={responsePair.id}
        className={classNames(
          "py-M",
          urlQueryId === responsePair.id ? "animate-highlight" : "",
        )}
      >
        <Typography variant="textM">{responsePair.question}</Typography>
        <div className="py-S">
          <TextAreaListItem
            text={text}
            responsePair={responsePair}
            onTextChange={onTextChange}
            editResponsePairKey={EDIT_RESPONSE_PAIR_KEY}
          />
        </div>

        <div className="flex justify-between">
          {!responsePair.response && (
            <div className="flex items-center gap-XS">
              <WarningCycleIcon className="text-redLight" />
              <Typography variant="textXS" className="text-redLight">
                {translate("QUESTIONNAIRE_ANSWERS/WARNING_NO_GENERATED_ANSWER")}
              </Typography>
            </div>
          )}

          {responsePair.response &&
            responsePair.lowConfidence &&
            !responsePair.manuallyEdited && (
              <div className="flex items-center gap-XS">
                <WarningCycleIcon className="text-warning" />
                <Typography variant="textXS" className="text-warning">
                  {translate(
                    "QUESTIONNAIRE_ANSWERS/WARNING_LOW_CONFIDENCE_ANSWER",
                  )}
                </Typography>
              </div>
            )}

          {(responsePair.lowConfidence || !responsePair.response) && (
            <div
              onClick={handleOpenShareModal}
              className={classNames("hover:cursor-pointer")}
            >
              <IconMessage className="text-grey5 hover:text-white" stroke={1} />
            </div>
          )}
        </div>

        {responsePair.response && (
          <AnswerSourceGroup citations={responsePair.citations} />
        )}
      </div>
    </>
  )
}
